import { Form, useForm } from '@/components/forms';
import Input from '@/components/forms/Input';
import Select from '@/components/forms/Select';
import { useDynamicLocale } from '@/hoslog-intl';
import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import { z } from 'zod';
import { useManageWithdrawals } from './affiliate/hooks/useWithdrawalsSetupMutation';
import { useWithdrawalSetupDetail } from './affiliate/hooks/useWithdrawalsSetupQuery';

const ManageWithdrawalSchema = z
  .object({
    minAmount: z.preprocess((val) => Number(val), z.number().min(1, { message: 'common.form.validation.required' })),
    maxAmount: z.preprocess((val) => Number(val), z.number().min(1, { message: 'common.form.validation.required' })),
    requestingDayFrom: z.string().refine((value) => value !== '' && value !== 'default', {
      message: 'common.form.validation.required',
    }),
    requestingDayTo: z.string().refine((value) => value !== '' && value !== 'default', {
      message: 'common.form.validation.required',
    }),
    withdrawableDate: z.string().refine((value) => value !== '' && value !== 'default', {
      message: 'common.form.validation.required',
    }),
  })
  .superRefine((data, ctx) => {
    if (data.minAmount >= data.maxAmount) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'common.form.validation.invalid', path: ['minAmount'] });
    }
    if (data.maxAmount <= data.minAmount) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'common.form.validation.invalid', path: ['maxAmount'] });
    }
  });

const requestingDayFromOptions = Array.from({ length: 27 }, (_, i) => ({
  label: `${i + 1}`,
  value: `${i + 1}`,
}));

const requestingDayToOptions = Array.from({ length: 27 }, (_, i) => ({
  label: `${i + 1}`,
  value: `${i + 1}`,
}));

interface ManageWithdrawalProps {
  withdrawalId?: string;
}

export const ManageWithdrawal = ({ withdrawalId }: ManageWithdrawalProps) => {
  const { withdrawalDetail, isLoading } = useWithdrawalSetupDetail(withdrawalId);
  const { mutateAsync } = useManageWithdrawals();
  const { t } = useDynamicLocale();

  const form = useForm({
    schema: ManageWithdrawalSchema,
    mode: 'onBlur',
    defaultValues: withdrawalDetail,
  });

  const requestingDateTo = useWatch({ control: form.control, name: 'requestingDayTo' });

  const withdrawableDateOptions = Array.from(
    { length: requestingDateTo ? 28 - Number(requestingDateTo) : 28 },
    (_, i) => ({
      label: requestingDateTo ? `${Number(requestingDateTo) + i + 1}` : `${i + 1}`,
      value: requestingDateTo ? `${Number(requestingDateTo) + i + 1}` : `${i + 1}`,
    }),
  );

  useEffect(() => {
    if (withdrawalDetail) {
      form.reset(withdrawalDetail);
    }
  }, [withdrawalDetail]);

  useEffect(() => {
    const subscription = form.watch((value, { name, type }) => {
      if (
        name === 'requestingDayTo' &&
        type === 'change' &&
        Number(value?.requestingDayTo) >= Number(value?.withdrawableDate)
      ) {
        form.setValue('withdrawableDate', String(Number(value.requestingDayTo) + 1));
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  return (
    <Form
      form={form}
      onSubmit={async (data) => {
        await mutateAsync({
          ...data,
          minAmount: Number(data.minAmount),
          maxAmount: Number(data.maxAmount),
          withdrawalId: withdrawalId || '',
        });
      }}
    >
      <fieldset disabled={isLoading}>
        <div className="w-full flex flex-col gap-4.5">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="configurationManagement.withdrawalSetup.affiliate.manageAffiliateWithdrawal.minAmount"
              {...form.register('minAmount')}
              placeholder={t(
                'configurationManagement.withdrawalSetup.manageWithdrawalModal.placeholder.enterMinAmount',
              )}
              type="text"
              inputMode="numeric"
              onlyIntegers
              maxLength={10}
              forceRequired
            />
            <Input
              label="configurationManagement.withdrawalSetup.affiliate.manageAffiliateWithdrawal.maxAmount"
              {...form.register('maxAmount')}
              placeholder={t(
                'configurationManagement.withdrawalSetup.manageWithdrawalModal.placeholder.enterMaxAmount',
              )}
              type="text"
              inputMode="numeric"
              onlyIntegers
              maxLength={10}
              forceRequired
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Select
              options={requestingDayFromOptions}
              label="configurationManagement.withdrawalSetup.manageWithdrawalModal.requestingDayFrom"
              placeholder={t('common.form.placeholder.select')}
              {...form.register('requestingDayFrom')}
            />
            <Select
              options={requestingDayToOptions}
              label="configurationManagement.withdrawalSetup.manageWithdrawalModal.requestingDayTo"
              placeholder={t('common.form.placeholder.select')}
              {...form.register('requestingDayTo')}
            />
          </div>
          <Select
            options={withdrawableDateOptions}
            label="configurationManagement.withdrawalSetup.manageWithdrawalModal.withdrawableDate"
            placeholder={t('common.form.placeholder.select')}
            {...form.register('withdrawableDate')}
            forceRequired
          />
        </div>
      </fieldset>
    </Form>
  );
};
